import type { ReactElement, ReactNode } from "react";
import { normalizeReferenceLabel, ReferenceChip } from "@/components/ui/reference-chip";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  getMentionActivationTitle,
  getMentionInteractionKind,
  type MentionInteractionContextValue,
  type ReferencePresentationResolver,
  referenceTargetKindFromVersionIntent,
} from "@/features/rich-text/mentions";
import {
  type MessageMentionReference,
  serializeMentionVersionRef,
} from "@/lib/message-mention-link";
import { flattenReactNodeText } from "@/lib/react-node-text";

/**
 * Renders one Markdown `a` node whose href is a canonical mention link, for every surface
 * that shows assistant text: the chat transcript, and the ask-user question and option text.
 * Sibling of {@link import("./message-markdown-evidence").renderDocumentEvidenceLink} — one
 * renderer per reference kind, so a mention looks and behaves the same wherever it appears.
 *
 * The chip carries the exact DOM contract the editor's `MentionNode` parses
 * (`data-type="mention"` plus `data-id` / `data-label` / `data-mention-type` /
 * `data-mention-version-ref`), so copying chat text and pasting it into the composer or a
 * document editor reproduces mention nodes instead of dropping the chips (LAT-901).
 *
 * `interactive: false` produces a plain chip with no role, tab stop, activation or tooltip.
 * It is what a chip inside an already-clickable host (an ask-user option button) needs: the
 * mention still reads as a mention, but nesting an interactive inside an interactive would
 * break both the accessibility tree and the host's click target.
 */
export function renderMessageMentionLink({
  children,
  interactive = true,
  mention,
  mentionInteraction,
  resolveReferencePresentation,
}: {
  children: ReactNode;
  interactive?: boolean;
  mention: MessageMentionReference;
  mentionInteraction: MentionInteractionContextValue | null;
  resolveReferencePresentation: ReferencePresentationResolver;
}): ReactElement {
  const childText = flattenReactNodeText(children).trim();
  const mentionLabel = childText.length > 0 ? childText : `@${mention.id}`;
  const normalizedLabel = normalizeReferenceLabel(mentionLabel);
  const interactionKind =
    mentionInteraction?.getInteractionKind(mention.mentionType) ??
    getMentionInteractionKind(mention.mentionType);
  // Without a provider there is nothing to activate, so an "interactive" caller still
  // degrades to a static chip rather than offering a link that goes nowhere.
  const isInteractive =
    interactive && Boolean(mentionInteraction) && interactionKind === "navigate";
  const targetKind = referenceTargetKindFromVersionIntent(mention.versionIntent);
  const versionRef = serializeMentionVersionRef(mention.versionIntent);
  const presentation = resolveReferencePresentation({
    kind: "mention",
    mentionType: mention.mentionType,
    targetKind,
  });

  const chip = (
    <ReferenceChip
      as="span"
      label={normalizedLabel}
      icon={presentation.Icon}
      interactive={isInteractive}
      referenceKind="mention"
      targetKind={targetKind ?? null}
      data-type="mention"
      data-mention-id={mention.id}
      data-id={mention.id}
      data-label={normalizedLabel}
      data-mention-type={mention.mentionType}
      data-mention-version-ref={versionRef ?? undefined}
      data-mention-interaction={interactionKind}
      role={isInteractive ? "link" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={
        isInteractive
          ? () => {
              void mentionInteraction?.activateMention(mention);
            }
          : undefined
      }
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              void mentionInteraction?.activateMention(mention);
            }
          : undefined
      }
    />
  );

  if (!interactive) {
    return chip;
  }

  const mentionTitle = getMentionActivationTitle({
    interactionKind,
    editable: false,
    enabled: isInteractive,
  });

  return (
    <Tooltip>
      <TooltipTrigger render={<span />}>{chip}</TooltipTrigger>
      <TooltipContent side="bottom">{mentionTitle}</TooltipContent>
    </Tooltip>
  );
}
